import { Dispatch } from 'react';
import { useDispatch } from 'react-redux';
import { Metadata, SchemaField } from '../../../types';
import { ElementAction } from '../../../elements/elementAction';
import { DataType } from '../../../enums/DataType';
import { FieldType } from '../../../enums/FieldType';
import { Functions } from '../../../types/types';
import { getInvalidItemDataReference, fieldIsRequired } from '../../../utils';
import { InvalidWorkspaceError } from '../../Alert/invalidWorkspaceError';
import { FullStoryManager } from '../../App/fullStory';
import { TriggerLocation } from '../../../enums/TriggerLocation';
import { FSEvent } from '../../../enums/FSEvent';
import { updateMetadataFormState } from '../../MetadataForm/metadataFormReducer';
import { ContentPlanRootState } from '../../../elements/contentPlan/store';
import { updateDataField, updateInvalidCells } from '../viewReducer';
import { Toasters } from '../../Toaster/toaster';
import { FieldData } from '../../../types/FieldData';

export const useActions = () => {
  const dispatch: Dispatch<any> = useDispatch();
  return {
    updateField: (data: Metadata, f: SchemaField, v: any) =>
      dispatch(updateField(data, f, v)),
    updateFields: (data: Metadata, fields: FieldData[]) =>
      dispatch(updateFields(data, fields)),
    updateData: (
      data: Metadata,
      f: SchemaField | FieldData[],
      v?: any,
      toasters?: Toasters<Partial<Metadata>>
    ) => dispatch(updateData(data, f, v, toasters))
  };
};

type S = ContentPlanRootState;

const updateField =
  (
    data: Metadata,
    field: SchemaField,
    value: any
  ): ElementAction<Functions, S> =>
  dispatch => {
    dispatch(updateFields(data, [{ field, value }]));
  };

const updateFields =
  (data: Metadata, fields: FieldData[]): ElementAction<Functions, S> =>
  (dispatch, getState) => {
    const { planning, view } = getState();
    const { selectedWorkspace } = planning;
    const schema = view?.selected?.dataSchema;
    if (!data) return;
    if (!data.props) data.props = {};
    if (selectedWorkspace?.disableItemDataRefIds?.length) {
      const props = { ...data.props };
      const oldProps = {};
      fields.forEach(({ field, value }) => {
        props[field.field] = value;
        oldProps[field.field] = data.props[field.field];
      });

      const invalidDataRef = getInvalidItemDataReference(
        selectedWorkspace,
        schema,
        { ...data, props }
      );
      if (!!invalidDataRef) {
        dispatch(updateMetadataFormState({ data: { ...data } }));
        throw new InvalidWorkspaceError('Failed to update item', {
          invalidDataRef,
          itemRefId: data.props.refId
        });
      }
    }
    fields.forEach(({ field, value }) => {
      data.props[field.field] = value;
    });
    dispatch(updateDataField(data?.props?.refId, data));
  };

const updateData =
  (
    data: Metadata,
    fields: SchemaField | FieldData[],
    value?: any,
    toasters?: any
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const handleInvalidServerResponse = (
      e: Error,
      refId: string,
      field: string
    ) => {
      if (e.name) {
        const cellIdentifier = [{ refId, field, serverSide: true }];
        dispatch(updateInvalidCells(cellIdentifier));
        if (toasters) {
          toasters.error({
            header: 'Could not save this change.',
            messages: [e.name]
          });
          toasters.toaster.autoHide(10000);
        }
      }
    };
    if (!Array.isArray(fields)) {
      if (fields.key == 'Item.inThePlan') {
        dispatch(
          FullStoryManager.getInstance().captureEvent(
            FSEvent.AddedOrRemovedFromThePlan,
            {
              from: !value,
              to: value,
              when: data?.props?.status?.[0]?.props?.description,
              triggerLocation: TriggerLocation.GRID_EDIT_MODE
            }
          )
        );
      }
      await dispatch(updateData(data, [{ field: fields, value }])).catch(
        async e => {
          handleInvalidServerResponse(e, data.props.refId, fields.key);
          throw e;
        }
      );
    }
    const { view, planning } = getState();
    const schema = view?.selected?.dataSchema;

    if (data?.id && Array.isArray(fields)) {
      const props = {};
      for (const { field, value } of fields) {
        let dataValue = data.props[field.field];

        if (
          fieldIsRequired(field, data) &&
          field.dataType != DataType.boolean &&
          (!dataValue || !dataValue?.length)
        ) {
          console.error(
            `Failed to update ${field?.name}, cannot delete a required field.`
          );
          return;
        }
        if (
          field.fieldType == FieldType.reference ||
          field.fieldType == FieldType.static
        ) {
          props[field.field] = value;
        }
      }
      if (Object.keys(props).length > 0) {
        const { updateData } = getAPI();
        try {
          await updateData({
            data: {
              labels: data.labels,
              props: {
                refId: data.props.refId,
                ...props
              }
            }
          });
        } catch (e) {
          handleInvalidServerResponse(e, data.props.refId, fields[0].field.key);
          throw e;
        }
      }
    }
  };
